// src/pages/HomePage.tsx
// Hero: accent badge · headline · progress card + streak · CTAs · stats ·
//        4-card athlete row with glowing curve · trust bar.
// Books section (Firestore query + BookCard list).

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuthStore }    from '../stores/useAuthStore';
import { useExerciseStore } from '../stores/useExerciseStore';
import { useThemePalette } from '../hooks/useThemePalette';
import { useThemeStore }   from '../stores/useThemeStore';
import { useT }            from '../data/translations';
import BookCard from '../components/books/BookCard';
import { StreakBadge } from '../components/streak/StreakBadge';
import {
  IconBook,
  IconChart,
  IconWifi,
  IconSparkle,
  IconRead,
  IconListen,
  IconLearn,
  IconImprove,
  IconArrow,
} from '../components/icons/Icons';

// ── Book type (extended with optional Firestore fields) ───────────────────────
interface BookDoc {
  id:           string;
  title:        string;
  description:  string;
  coverUrl:     string;
  displayOrder: number;
  status:       'live' | 'draft';
  units:        number;
  level?:       string;
  tier?:        'free' | 'premium';
  unitTitles?:  string[];
}

// ── Athlete cards — SVG icons, zero emoji ─────────────────────────────────────
const ATHLETE_CARDS: Array<{
  src:    string;
  label:  string;
  Icon:   React.ComponentType<{ size?: number; color?: string }>;
  ty:     string;
  rot:    string;
  stat:   string;
  statLabel: string;
}> = [
  { src: '/images/Book.png', label: 'Read',    Icon: IconRead,    ty: '14px',  rot: '-3deg',  stat: '', statLabel: 'Mindset'    },
  { src: '/images/coach.png', label: 'Listen',  Icon: IconListen,  ty: '-8px',  rot: '2.5deg', stat: '', statLabel: 'Coaching'   },
  { src: '/images/fitness2.png', label: 'Learn',   Icon: IconLearn,   ty: '6px',   rot: '-2deg',  stat: '', statLabel: 'Growth'     },
  { src: '/images/rehabilitation.png', label: 'Improve', Icon: IconImprove, ty: '-2px',  rot: '3deg',   stat: '', statLabel: 'Resilience' },
];

const TRUST_AVATARS = [
  '/images/athlete1.png',
  '/images/athlete2.png',
  '/images/athlete3.png',
  '/images/athlete4.png',
  '/images/athlete1.png',
];

// ── Accent purple (non-palette) ───────────────────────────────────────────────
const PURPLE = '#4f3ff5';

// ── Keyframe injection ────────────────────────────────────────────────────────
(function injectStyles() {
  if (typeof document === 'undefined') return;
  const id = 'esp-hero-kf';
  if (document.getElementById(id)) return;
  const s = document.createElement('style');
  s.id = id;
  s.textContent = `
    @keyframes heroEnter {
      from { opacity: 0; transform: translateY(26px); }
      to   { opacity: 1; transform: translateY(0);    }
    }
    @keyframes fadeIn {
      from { opacity: 0; }
      to   { opacity: 1; }
    }
    @keyframes floatCard {
      0%, 100% { transform: var(--card-transform); }
      50%      { transform: var(--card-transform-mid); }
    }
    @keyframes pulseGlow {
      0%, 100% { opacity: 0.55; }
      50%      { opacity: 1.00; }
    }
    @keyframes pulseGlow2 {
      0%, 100% { opacity: 0.3; transform: scale(1) rotate(0deg); }
      33%      { opacity: 0.7; transform: scale(1.05) rotate(60deg); }
      66%      { opacity: 0.45; transform: scale(0.96) rotate(120deg); }
    }
    @keyframes esp-spin {
      to { transform: rotate(360deg); }
    }
    @keyframes ringDash {
      from { stroke-dashoffset: 183; }
      to   { stroke-dashoffset: 46;  }
    }
    @keyframes meshFloat1 {
      0%, 100% { transform: translate(0, 0) scale(1); }
      33%      { transform: translate(-55px, 70px) scale(1.07); }
      66%      { transform: translate(38px, -48px) scale(0.94); }
    }
    @keyframes meshFloat2 {
      0%, 100% { transform: translate(0, 0) scale(1); }
      40%      { transform: translate(65px, -62px) scale(1.11); }
      70%      { transform: translate(-32px, 42px) scale(0.92); }
    }
    @keyframes meshFloat3 {
      0%, 100% { transform: translate(0, 0) scale(1); }
      50%      { transform: translate(-68px, -34px) scale(1.06); }
    }
    @keyframes floatY {
      0%, 100% { transform: translateY(0px) rotate(var(--obj-rot, 0deg)); }
      50%       { transform: translateY(-14px) rotate(var(--obj-rot, 0deg)); }
    }
    @keyframes floatRunner {
      0%, 100% { transform: translateY(0px) scaleX(1); }
      50%       { transform: translateY(-10px) scaleX(1); }
    }
    @keyframes auraGlow {
      0%, 100% { opacity: 0.18; transform: scaleX(1); }
      50%       { opacity: 0.38; transform: scaleX(1.12); }
    }
    @keyframes particleDrift {
      0%   { transform: translate(0, 0) scale(1);   opacity: var(--p-opacity); }
      33%  { transform: translate(var(--px), var(--py)) scale(1.4); opacity: calc(var(--p-opacity) * 1.6); }
      66%  { transform: translate(calc(var(--px) * -0.5), calc(var(--py) * 1.3)) scale(0.8); opacity: calc(var(--p-opacity) * 0.6); }
      100% { transform: translate(0, 0) scale(1);   opacity: var(--p-opacity); }
    }
    /* Seamless pulse-travel: dash enters from path start, exits at path end,
       then fades out before the next loop begins — no mid-path reset jerk.
       stroke-dasharray uses a huge gap so only one lit segment is ever visible. */
    @keyframes ecPulse1 {
      0%   { stroke-dashoffset:  180;  opacity: 0;   }
      6%   { opacity: 0.9; }
      82%  { opacity: 0.9; }
      92%  { stroke-dashoffset: -980;  opacity: 0;   }
      100% { stroke-dashoffset: -980;  opacity: 0;   }
    }
    @keyframes ecPulse2 {
      0%   { stroke-dashoffset:  140;  opacity: 0;   }
      7%   { opacity: 0.7; }
      83%  { opacity: 0.7; }
      93%  { stroke-dashoffset: -920;  opacity: 0;   }
      100% { stroke-dashoffset: -920;  opacity: 0;   }
    }
    @keyframes ecPulse3 {
      0%   { stroke-dashoffset:  110;  opacity: 0;   }
      8%   { opacity: 0.65; }
      84%  { opacity: 0.65; }
      94%  { stroke-dashoffset: -860;  opacity: 0;   }
      100% { stroke-dashoffset: -860;  opacity: 0;   }
    }
    /* Tilted ellipse orbit ring — preserves initial rotation offset */
    @keyframes orbitRingTilt {
      from { transform: translate(-50%, -50%) rotate(-18deg); }
      to   { transform: translate(-50%, -50%) rotate(342deg); }
    }
    /* Sport asset float — eases both directions, never jumps */
    @keyframes sportFloat {
      0%, 100% { transform: translateY(0px)    rotate(var(--sf-rot, 0deg)); }
      30%      { transform: translateY(-10px)  rotate(var(--sf-rot, 0deg)); }
      60%      { transform: translateY(-4px)   rotate(var(--sf-rot, 0deg)); }
    }
    @keyframes statCardFloat {
      0%, 100% { transform: translateY(0px); }
      50%       { transform: translateY(-6px); }
    }
    @keyframes statCardEnter {
      from { opacity: 0; transform: scale(0.85) translateY(10px); }
      to   { opacity: 1; transform: scale(1)    translateY(0);    }
    }
    @keyframes buttonShimmer {
      0%   { background-position: -200% center; }
      100% { background-position: 200% center; }
    }
    @keyframes orbitRing {
      from { transform: translate(-50%, -50%) rotate(0deg); }
      to   { transform: translate(-50%, -50%) rotate(360deg); }
    }
    @media (prefers-reduced-motion: reduce) {
      *, *::before, *::after {
        animation-duration: 0.01ms !important;
        animation-iteration-count: 1 !important;
        transition-duration: 0.01ms !important;
      }
    }
  `;
  document.head.appendChild(s);
})();

// ── Particles backdrop ────────────────────────────────────────────────────────
function ParticleField({ isDark }: { isDark: boolean }) {
  const particles = React.useMemo(() => {
    const rng = (min: number, max: number) => min + Math.random() * (max - min);
    return Array.from({ length: 28 }, (_, i) => ({
      id: i,
      left: `${rng(0, 100)}%`,
      top:  `${rng(0, 100)}%`,
      size: rng(1, 3.2),
      opacity: rng(0.06, 0.28),
      dur:  `${rng(8, 22)}s`,
      delay:`${rng(0, 12)}s`,
      px:   `${rng(-30, 30)}px`,
      py:   `${rng(-30, 30)}px`,
    }));
  }, []);

  return (
    <div aria-hidden="true" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0, overflow: 'hidden' }}>
      {particles.map(p => (
        <div
          key={p.id}
          style={{
            position:        'absolute',
            left:            p.left,
            top:             p.top,
            width:           `${p.size}px`,
            height:          `${p.size}px`,
            borderRadius:    '50%',
            background:      isDark
              ? `radial-gradient(circle, rgba(168,133,247,0.9), rgba(79,140,247,0.5))`
              : `radial-gradient(circle, rgba(79,63,245,0.7), rgba(79,140,247,0.4))`,
            boxShadow:       isDark ? `0 0 ${p.size * 2.5}px rgba(168,133,247,0.6)` : 'none',
            ['--p-opacity' as never]: p.opacity,
            ['--px' as never]:        p.px,
            ['--py' as never]:        p.py,
            animation:       `particleDrift ${p.dur} ease-in-out ${p.delay} infinite`,
            opacity:         p.opacity,
          } as React.CSSProperties}
        />
      ))}
    </div>
  );
}

// ── Energy curves SVG ─────────────────────────────────────────────────────────
// Blur filters on animated SVG paths force per-frame CPU rasterisation — we
// replaced them with a wider semi-transparent "soft glow" stroke so the whole
// animation stays on the GPU compositor.
function EnergyCurves({ C }: { C: ReturnType<typeof useThemePalette> }) {
  const pulseStyle = (anim: string): React.CSSProperties => ({
    animation:  anim,
    willChange: 'stroke-dashoffset, opacity',
  });
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 500 560"
      style={{
        position:      'absolute',
        inset:         '-5%',
        width:         '110%',
        height:        '110%',
        pointerEvents: 'none',
        zIndex:        0,
        overflow:      'visible',
      }}
    >
      <defs>
        <linearGradient id="ec-g1" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%"   stopColor={C.accent}  stopOpacity="0" />
          <stop offset="30%"  stopColor={C.accent}  stopOpacity="0.9" />
          <stop offset="60%"  stopColor={C.accent2} stopOpacity="1"   />
          <stop offset="100%" stopColor={PURPLE}     stopOpacity="0"   />
        </linearGradient>
        <linearGradient id="ec-g2" x1="0" y1="1" x2="1" y2="0">
          <stop offset="0%"   stopColor={PURPLE}     stopOpacity="0"   />
          <stop offset="40%"  stopColor={C.accent3}  stopOpacity="0.8" />
          <stop offset="70%"  stopColor={C.accent2}  stopOpacity="0.9" />
          <stop offset="100%" stopColor={C.accent}   stopOpacity="0"   />
        </linearGradient>
        <linearGradient id="ec-g3" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%"   stopColor={C.accent2}  stopOpacity="0"   />
          <stop offset="50%"  stopColor={C.accent3}  stopOpacity="0.7" />
          <stop offset="100%" stopColor={PURPLE}      stopOpacity="0"   />
        </linearGradient>
      </defs>

      {/* Curve 1 — soft glow (wide, low-opacity, no filter) + bright core */}
      <path d="M -20 420 C 80 120, 200 480, 310 240 S 440 60, 520 300"
        fill="none" stroke="url(#ec-g1)" strokeWidth="18" strokeLinecap="round"
        strokeDasharray="160 9999" opacity="0.28"
        style={pulseStyle('ecPulse1 8s ease-in-out infinite')} />
      <path d="M -20 420 C 80 120, 200 480, 310 240 S 440 60, 520 300"
        fill="none" stroke="url(#ec-g1)" strokeWidth="2" strokeLinecap="round"
        strokeDasharray="160 9999"
        style={pulseStyle('ecPulse1 8s ease-in-out infinite')} />

      {/* Curve 2 */}
      <path d="M 520 80 C 400 300, 260 -30, 160 220 S 30 440, -20 200"
        fill="none" stroke="url(#ec-g2)" strokeWidth="14" strokeLinecap="round"
        strokeDasharray="120 9999" opacity="0.24"
        style={pulseStyle('ecPulse2 11s ease-in-out 2.5s infinite')} />
      <path d="M 520 80 C 400 300, 260 -30, 160 220 S 30 440, -20 200"
        fill="none" stroke="url(#ec-g2)" strokeWidth="1.5" strokeLinecap="round"
        strokeDasharray="120 9999"
        style={pulseStyle('ecPulse2 11s ease-in-out 2.5s infinite')} />

      {/* Curve 3 */}
      <path d="M 0 560 C 120 380, 300 480, 400 280 S 480 100, 520 -20"
        fill="none" stroke="url(#ec-g3)" strokeWidth="10" strokeLinecap="round"
        strokeDasharray="100 9999" opacity="0.22"
        style={pulseStyle('ecPulse3 14s ease-in-out 5.5s infinite')} />
      <path d="M 0 560 C 120 380, 300 480, 400 280 S 480 100, 520 -20"
        fill="none" stroke="url(#ec-g3)" strokeWidth="1.2" strokeLinecap="round"
        strokeDasharray="100 9999"
        style={pulseStyle('ecPulse3 14s ease-in-out 5.5s infinite')} />
    </svg>
  );
}

// ── Floating decorative sports objects ────────────────────────────────────────
// Each sport asset used ONCE; sized 90–110 px; orbiting near the right-column
// orbit ring as external companions (not inside it).
function FloatingObjects({ isDark }: { isDark: boolean }) {
  const glowPurple = isDark ? 'rgba(79,63,245,0.60)' : 'rgba(79,63,245,0.28)';
  const glowAmber  = isDark ? 'rgba(251,191,36,0.45)' : 'rgba(217,119,6,0.28)';
  const glowBlue   = isDark ? 'rgba(79,140,247,0.50)' : 'rgba(37,99,235,0.28)';
  return (
    <div aria-hidden="true" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0, overflow: 'hidden' }}>

      {/* Football — top-right, floating near orbit ring upper arc */}
      <div style={{
        position:  'absolute',
        top:       '7%',
        right:     '3%',
        width:     '100px',
        height:    '100px',
        ['--sf-rot' as never]: '-10deg',
        animation: 'sportFloat 7s ease-in-out infinite',
        filter:    `drop-shadow(0 0 22px ${glowPurple}) drop-shadow(0 4px 12px rgba(0,0,0,0.4))`,
      } as React.CSSProperties}>
        <img src="/images/football.png" alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
      </div>

      {/* Basketball — lower-right, below the athlete card grid */}
      <div style={{
        position:  'absolute',
        bottom:    '18%',
        right:     '1%',
        width:     '92px',
        height:    '92px',
        ['--sf-rot' as never]: '8deg',
        animation: 'sportFloat 8.5s ease-in-out 1.6s infinite',
        filter:    `drop-shadow(0 0 18px ${glowAmber}) drop-shadow(0 4px 10px rgba(0,0,0,0.35))`,
      } as React.CSSProperties}>
        {/* Soft aura ring */}
        <div style={{
          position:     'absolute',
          inset:        '-10px',
          borderRadius: '50%',
          background:   `radial-gradient(ellipse 110% 110% at 50% 50%, rgba(251,191,36,0.22) 0%, transparent 70%)`,
          filter:       'blur(8px)',
          animation:    'auraGlow 8.5s ease-in-out 1.6s infinite',
        }} />
        <img src="/images/basketball.png" alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', position: 'relative', zIndex: 1 }} />
      </div>

      {/* Runner — bottom area, floating near orbit ring lower arc */}
      <div style={{
        position:  'absolute',
        bottom:    '9%',
        right:     '20%',
        width:     '108px',
        height:    '108px',
        ['--sf-rot' as never]: '0deg',
        animation: 'sportFloat 6.2s ease-in-out 0.8s infinite',
      } as React.CSSProperties}>
        {/* Speed aura */}
        <div style={{
          position:     'absolute',
          inset:        '-10px',
          borderRadius: '50%',
          background:   `radial-gradient(ellipse 140% 60% at 60% 50%, rgba(79,140,247,0.32) 0%, transparent 70%)`,
          filter:       'blur(12px)',
          animation:    'auraGlow 6.2s ease-in-out 0.8s infinite',
        }} />
        <img src="/images/runner.png" alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', position: 'relative', zIndex: 1,
          filter: `drop-shadow(0 0 16px ${glowBlue})` }} />
      </div>

    </div>
  );
}

// ── Floating premium stat card ────────────────────────────────────────────────
function FloatStatCard({
  label, value, sub, pos, delay, C, isDark,
}: {
  label: string; value: string; sub: string;
  pos: React.CSSProperties; delay: string;
  C: ReturnType<typeof useThemePalette>; isDark: boolean;
}) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position:             'absolute',
        ...pos,
        padding:              '10px 14px',
        borderRadius:         '14px',
        background:           isDark
          ? 'rgba(13,18,32,0.72)'
          : 'rgba(255,255,255,0.82)',
        backdropFilter:       'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        border:               `1px solid ${hover
          ? (isDark ? 'rgba(124,111,247,0.45)' : 'rgba(79,63,245,0.30)')
          : (isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)')}`,
        boxShadow:            hover
          ? `0 0 0 1px rgba(124,111,247,0.2), 0 12px 32px rgba(79,63,245,0.25)`
          : `0 4px 20px rgba(0,0,0,0.28)`,
        zIndex:               3,
        minWidth:             '106px',
        animation:            `statCardEnter 0.6s ease ${delay} both, statCardFloat ${5 + parseFloat(delay) * 0.7}s ease-in-out ${delay} infinite`,
        transition:           'border-color 0.2s, box-shadow 0.2s',
        cursor:               'default',
      }}
    >
      <div style={{
        fontSize:      '0.62rem',
        fontWeight:    700,
        letterSpacing: '0.10em',
        textTransform: 'uppercase',
        color:         C.muted,
        marginBottom:  '4px',
      }}>
        {label}
      </div>
      <div style={{
        fontSize:             '1.18rem',
        fontWeight:           900,
        letterSpacing:        '-0.02em',
        background:           `linear-gradient(135deg, ${C.accent} 0%, ${C.accent2} 100%)`,
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor:  'transparent',
        backgroundClip:       'text',
        lineHeight:           1.1,
      }}>
        {value}
      </div>
      <div style={{
        fontSize:   '0.64rem',
        color:      C.muted,
        marginTop:  '2px',
        fontWeight: 500,
      }}>
        {sub}
      </div>
    </div>
  );
}

// ── Hero progress ring (animated fill) ────────────────────────────────────────
function HeroRing({ percent }: { percent: number }) {
  const C    = useThemePalette();
  const size = 58, sw = 5;
  const r    = (size - sw) / 2;
  const circ = 2 * Math.PI * r;
  const pct  = Math.min(Math.max(percent, 0), 100) / 100;
  const off  = circ * (1 - pct);
  const c    = size / 2;
  return (
    <svg
      width={size} height={size} viewBox={`0 0 ${size} ${size}`}
      style={{ display: 'block', flexShrink: 0 }}
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="hp-ring-grad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%"   stopColor={PURPLE}   />
          <stop offset="100%" stopColor={C.accent2} />
        </linearGradient>
        <filter id="hp-ring-glow">
          <feGaussianBlur stdDeviation="1.5" result="blur" />
          <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
      </defs>
      {/* Track */}
      <circle cx={c} cy={c} r={r} fill="none" stroke={C.border} strokeWidth={sw} />
      {/* Filled arc — animated on mount */}
      <circle
        cx={c} cy={c} r={r} fill="none"
        stroke="url(#hp-ring-grad)" strokeWidth={sw} strokeLinecap="round"
        strokeDasharray={`${circ} ${circ}`}
        strokeDashoffset={circ}
        filter="url(#hp-ring-glow)"
        style={{
          transform:       'rotate(-90deg)',
          transformOrigin: 'center',
          animation:       `ringDash 1.2s ease 0.6s forwards`,
          '--ring-off':    `${off}`,
        } as React.CSSProperties}
      >
        <animate
          attributeName="stroke-dashoffset"
          from={`${circ}`}
          to={`${off}`}
          dur="1.2s"
          begin="0.6s"
          fill="freeze"
          calcMode="spline"
          keySplines="0.25 0.46 0.45 0.94"
        />
      </circle>
      <text x={c} y={c} textAnchor="middle" dominantBaseline="central"
        fill={C.text} fontSize="12" fontWeight="800" fontFamily="inherit">
        {percent}%
      </text>
    </svg>
  );
}

// ── Stat mini-block ───────────────────────────────────────────────────────────
function StatBlock({ icon, num, label }: { icon: React.ReactNode; num: string; label: string }) {
  const C = useThemePalette();
  const [hover, setHover] = useState(false);
  return (
    <div
      style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'default' }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <div style={{
        width:          '40px',
        height:         '40px',
        borderRadius:   '11px',
        background:     hover ? 'rgba(79,140,247,0.18)' : 'rgba(79,140,247,0.08)',
        border:         `1px solid ${hover ? 'rgba(79,140,247,0.40)' : 'rgba(79,140,247,0.16)'}`,
        display:        'flex',
        alignItems:     'center',
        justifyContent: 'center',
        flexShrink:     0,
        boxShadow:      hover ? '0 0 14px rgba(79,140,247,0.30)' : 'none',
        transition:     'background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
      }}>
        {icon}
      </div>
      <div>
        <div style={{ fontWeight: 800, fontSize: '1.0rem', color: C.text, lineHeight: 1.2 }}>
          {num}
        </div>
        <div style={{
          fontSize:      '0.67rem',
          color:         C.muted,
          fontWeight:    600,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          marginTop:     '2px',
        }}>
          {label}
        </div>
      </div>
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function HomePage() {
  const C      = useThemePalette();
  const isDark = useThemeStore((s) => s.theme) === 'dark';
  const t      = useT();

  const authLoaded  = useAuthStore((s) => s.authLoaded);

  const [books,   setBooks]   = useState<BookDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);

  const [primaryHov, setPrimaryHov] = useState(false);
  const [ghostHov,   setGhostHov]   = useState(false);

  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' ? window.innerWidth < 768 : false,
  );

  const [inView, setInView] = useState({
    trustBar:     false,
    booksSection: false,
  });

  const booksRef        = useRef<HTMLDivElement>(null);
  const skipRef         = useRef<HTMLAnchorElement>(null);
  const trustBarRef     = useRef<HTMLDivElement>(null);
  const booksSectionRef = useRef<HTMLDivElement>(null);

  // Real-time progress data for computed hero stats
  const progressData = useExerciseStore(s => s.progressData);

  const overallPct = useMemo(() => {
    const percs: number[] = [];
    for (const unitProg of Object.values(progressData)) {
      for (const sp of Object.values(unitProg)) {
        percs.push(sp.percentage ?? 0);
      }
    }
    return percs.length > 0
      ? Math.round(percs.reduce((a, b) => a + b, 0) / percs.length)
      : 0;
  }, [progressData]);

  const sectionsCompleted = useMemo(() => {
    let n = 0;
    for (const unitProg of Object.values(progressData)) {
      for (const sp of Object.values(unitProg)) {
        if (sp.completedFlag) n++;
      }
    }
    return n;
  }, [progressData]);

  const exercisesDone = useMemo(() => {
    let n = 0;
    for (const unitProg of Object.values(progressData)) {
      for (const sp of Object.values(unitProg)) {
        n += sp.completed ?? 0;
      }
    }
    return n;
  }, [progressData]);

  const unitsInProgress = useMemo(() => {
    let n = 0;
    for (const unitProg of Object.values(progressData)) {
      if (Object.values(unitProg).some(sp => (sp.completed ?? 0) > 0 || sp.completedFlag)) n++;
    }
    return n;
  }, [progressData]);

  const floatStats = useMemo(() => [
    { label: 'Books Available', value: String(books.length),      sub: 'across all levels', pos: { top: '3%',    right: '4%' } as React.CSSProperties, delay: '0s'   },
    { label: 'Units Active',    value: String(unitsInProgress),   sub: 'in progress',       pos: { top: '3%',    left: '4%'  } as React.CSSProperties, delay: '0.4s' },
    { label: 'Sections Done',   value: String(sectionsCompleted), sub: 'completed',         pos: { bottom: '5%', left: '4%' } as React.CSSProperties, delay: '0.8s' },
    { label: 'Exercises Done',  value: String(exercisesDone),     sub: 'total answered',    pos: { bottom: '5%', right: '4%'} as React.CSSProperties, delay: '1.2s' },
  ], [books.length, unitsInProgress, sectionsCompleted, exercisesDone]);

  // Responsive listener
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize, { passive: true });
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Scroll-reveal IntersectionObserver
  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    const observe = (el: Element | null, key: keyof typeof inView) => {
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setInView(v => ({ ...v, [key]: true }));
            obs.disconnect();
          }
        },
        { threshold: 0.12 },
      );
      obs.observe(el);
      observers.push(obs);
    };
    observe(trustBarRef.current,     'trustBar');
    observe(booksSectionRef.current, 'booksSection');
    return () => observers.forEach(o => o.disconnect());
  }, []);

  // Books fetch — runs only once auth state is settled
  useEffect(() => {
    if (!authLoaded) return;
    let cancelled = false;
    const fetchBooks = async () => {
      setLoading(true);
      setError(null);
      try {
        const q    = query(collection(db, 'books'), orderBy('displayOrder', 'asc'));
        const snap = await getDocs(q);
        if (cancelled) return;
        const raw = snap.docs.map((d) => ({ id: d.id, ...d.data() } as BookDoc));
        setBooks(raw.filter((b) => b.status === 'live'));
      } catch (err) {
        if (!cancelled) {
          console.error('[HomePage] books fetch failed:', err);
          setError('Could not load books. Please check your connection and try again.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void fetchBooks();
    return () => { cancelled = true; };
  }, [authLoaded]);

  const scrollToBooks = () =>
    booksRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const retryFetch = () => {
    setError(null);
    setLoading(true);
    const q = query(collection(db, 'books'), orderBy('displayOrder', 'asc'));
    getDocs(q)
      .then(snap => {
        const raw = snap.docs.map(d => ({ id: d.id, ...d.data() } as BookDoc));
        setBooks(raw.filter(b => b.status === 'live'));
      })
      .catch(e => setError((e as Error).message))
      .finally(() => setLoading(false));
  };

  const heroAnim = (delayMs: number): React.CSSProperties => ({
    animation:      `heroEnter 0.65s ease both`,
    animationDelay: `${delayMs}ms`,
  });

  const revealStyle = (visible: boolean, delayMs = 0): React.CSSProperties => ({
    opacity:    visible ? 1 : 0,
    transform:  visible ? 'translateY(0)' : 'translateY(18px)',
    transition: `opacity 0.75s ease ${delayMs}ms, transform 0.75s ease ${delayMs}ms`,
  });

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div style={{
      minHeight:  '100vh',
      background: C.bg,
      color:      C.text,
      fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
      transition: 'background 0.25s ease, color 0.25s ease',
    }}>

      {/* Skip to content — keyboard a11y */}
      <a
        ref={skipRef}
        href="#books-section"
        style={{
          position:       'absolute',
          top:            '-9999px',
          left:           '16px',
          zIndex:         200,
          padding:        '8px 16px',
          background:     C.accent,
          color:          '#fff',
          borderRadius:   '0 0 8px 8px',
          fontWeight:     700,
          fontSize:       '0.85rem',
          textDecoration: 'none',
        }}
        onFocus={(e) => { (e.currentTarget as HTMLAnchorElement).style.top = '0'; }}
        onBlur={(e)  => { (e.currentTarget as HTMLAnchorElement).style.top = '-9999px'; }}
      >
        Skip to books
      </a>

      {/* ════════════════════════════ HERO ═══════════════════════════════ */}
      <section
        aria-label="Hero"
        style={{
          position:      'relative',
          overflow:      'hidden',
          maxWidth:      '1280px',
          margin:        '0 auto',
          padding:       isMobile ? '52px 24px 0' : '80px 48px 0',
          display:       'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap:           isMobile ? '36px' : '48px',
          alignItems:    'center',
          minHeight:     isMobile ? 'auto' : 'calc(100vh - 64px)',
        }}
      >

        {/* ── Animated mesh background orbs ── */}
        <div aria-hidden="true" style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 0 }}>
          <div style={{
            position: 'absolute', top: '-8%', left: '5%',
            width: '680px', height: '680px', borderRadius: '50%',
            background: `radial-gradient(circle, rgba(79,63,245,0.18) 0%, rgba(79,140,247,0.07) 45%, transparent 70%)`,
            filter: 'blur(42px)', animation: 'meshFloat1 18s ease-in-out infinite',
          }} />
          <div style={{
            position: 'absolute', top: '30%', right: '-8%',
            width: '520px', height: '520px', borderRadius: '50%',
            background: `radial-gradient(circle, rgba(124,111,247,0.16) 0%, rgba(168,85,247,0.08) 45%, transparent 70%)`,
            filter: 'blur(52px)', animation: 'meshFloat2 22s ease-in-out infinite',
          }} />
          <div style={{
            position: 'absolute', bottom: '5%', left: '28%',
            width: '420px', height: '420px', borderRadius: '50%',
            background: `radial-gradient(circle, rgba(79,140,247,0.12) 0%, transparent 70%)`,
            filter: 'blur(60px)', animation: 'meshFloat3 26s ease-in-out infinite',
          }} />

          {/* Extra accent orb for depth */}
          <div style={{
            position: 'absolute', top: '15%', right: '30%',
            width: '300px', height: '300px', borderRadius: '50%',
            background: `radial-gradient(circle, rgba(168,85,247,0.10) 0%, transparent 70%)`,
            filter: 'blur(50px)', animation: 'meshFloat1 14s ease-in-out 3s infinite',
          }} />
        </div>

        {/* ── Particles ── */}
        <ParticleField isDark={isDark} />

        {/* ── Floating sports objects (desktop only) ── */}
        {!isMobile && <FloatingObjects isDark={isDark} />}

        {/* ── LEFT COLUMN ── */}
        <div style={{ flex: '0 0 56%', display: 'flex', flexDirection: 'column', gap: '28px', zIndex: 1 }}>

          {/* Eyebrow badge */}
          <div style={{
            display:       'inline-flex',
            alignItems:    'center',
            alignSelf:     'flex-start',
            gap:           '8px',
            padding:       '8px 16px',
            borderRadius:  '999px',
            background:    `linear-gradient(135deg, rgba(79,63,245,0.16), rgba(124,111,247,0.12))`,
            border:        '1px solid rgba(124,111,247,0.35)',
            color:         C.accent2,
            fontSize:      '0.72rem',
            fontWeight:    700,
            letterSpacing: '0.10em',
            textTransform: 'uppercase',
            boxShadow:     isDark ? '0 0 18px rgba(124,111,247,0.14)' : 'none',
            ...heroAnim(100),
          }}>
            <IconSparkle size={13} color={C.accent2} />
            {t('HERO_BADGE')}
          </div>

          {/* Heading */}
          <h1 style={{
            fontSize:      isMobile ? 'clamp(2.1rem, 10vw, 3.2rem)' : 'clamp(2.8rem, 5vw, 4.8rem)',
            fontWeight:    900,
            lineHeight:    1.05,
            margin:        0,
            letterSpacing: '-0.028em',
            color:         C.text,
            ...heroAnim(220),
          }}>
            {t('HERO_TITLE_LINE1')}<br />
            <span style={{
              background:           `linear-gradient(135deg, ${C.accent} 0%, ${C.accent2} 45%, ${PURPLE} 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor:  'transparent',
              backgroundClip:       'text',
              filter:               isDark ? 'drop-shadow(0 0 24px rgba(124,111,247,0.5))' : 'none',
            }}>
              {t('HERO_TITLE_LINE2')}
            </span>
          </h1>

          {/* Subtitle */}
          <p style={{
            fontSize:   '1.05rem',
            lineHeight: 1.82,
            color:      C.muted,
            maxWidth:   '520px',
            margin:     0,
            ...heroAnim(380),
          }}>
            {t('HERO_SUBTITLE')}
          </p>

          {/* Progress card + streak pill */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', ...heroAnim(520) }}>
            <div style={{
              display:              'flex',
              alignItems:           'center',
              gap:                  '16px',
              background:           isDark
                ? 'rgba(13,18,32,0.65)'
                : C.card,
              border:               `1px solid ${isDark ? 'rgba(255,255,255,0.09)' : C.border}`,
              borderRadius:         '18px',
              padding:              '14px 20px',
              backdropFilter:       'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              flex:                 '1 1 auto',
              maxWidth:             '380px',
              boxShadow:            isDark
                ? '0 0 0 1px rgba(124,111,247,0.08), 0 8px 28px rgba(0,0,0,0.35)'
                : '0 4px 16px rgba(0,0,0,0.06)',
              transition:           'background 0.25s ease, border-color 0.25s ease',
            }}>
              <HeroRing percent={overallPct} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontWeight: 700, fontSize: '0.92rem', color: C.text, margin: '0 0 3px' }}>
                  Your Progress
                </p>
                <p style={{ fontSize: '0.78rem', color: C.muted, margin: 0, lineHeight: 1.5 }}>
                  Keep going! You're building your future in sport.
                </p>
              </div>
            </div>

            {/* Streak pill */}
            <StreakBadge size="md" />
          </div>

          {/* CTA buttons */}
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', ...heroAnim(660) }}>
            {/* Primary CTA */}
            <button
              style={{
                padding:          '15px 30px',
                borderRadius:     '13px',
                background:       primaryHov
                  ? `linear-gradient(135deg, ${C.accent} 0%, ${C.accent2} 50%, ${PURPLE} 100%)`
                  : `linear-gradient(135deg, ${C.accent} 0%, ${C.accent2} 100%)`,
                color:            '#fff',
                fontWeight:       700,
                fontSize:         '0.97rem',
                border:           'none',
                cursor:           'pointer',
                fontFamily:       'inherit',
                transition:       'transform 0.18s ease, box-shadow 0.18s ease, filter 0.18s ease, background 0.25s ease',
                transform:        primaryHov ? 'translateY(-4px) scale(1.02)' : 'none',
                boxShadow:        primaryHov
                  ? `0 0 0 4px rgba(79,63,245,0.18), 0 20px 50px rgba(79,63,245,0.58)`
                  : `0 6px 24px rgba(79,140,247,0.32)`,
                filter:           primaryHov ? 'brightness(1.10)' : 'none',
                display:          'inline-flex',
                alignItems:       'center',
                gap:              '8px',
                outline:          'none',
                letterSpacing:    '0.01em',
              }}
              onMouseEnter={() => setPrimaryHov(true)}
              onMouseLeave={() => setPrimaryHov(false)}
              onClick={scrollToBooks}
              onFocus={() => setPrimaryHov(true)}
              onBlur={()  => setPrimaryHov(false)}
              onKeyDown={(e) => { if (e.key === 'Enter') scrollToBooks(); }}
            >
              {t('HERO_CTA1')}
              <span style={{
                display:    'inline-block',
                transform:  primaryHov ? 'translateX(4px)' : 'none',
                transition: 'transform 0.18s ease',
                lineHeight: 1,
              }}>
                <IconArrow size={16} color="#fff" />
              </span>
            </button>

            {/* Ghost CTA */}
            <button
              style={{
                padding:      '15px 30px',
                borderRadius: '13px',
                background:   ghostHov
                  ? (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)')
                  : 'transparent',
                color:        C.text,
                fontWeight:   600,
                fontSize:     '0.97rem',
                border:       `1px solid ${ghostHov
                  ? (isDark ? 'rgba(255,255,255,0.26)' : 'rgba(0,0,0,0.20)')
                  : C.border}`,
                boxShadow:    ghostHov
                  ? (isDark ? '0 4px 20px rgba(255,255,255,0.05)' : '0 4px 16px rgba(0,0,0,0.06)')
                  : 'none',
                cursor:       'pointer',
                fontFamily:   'inherit',
                transition:   'border-color 0.18s, background 0.18s, box-shadow 0.18s, transform 0.18s',
                transform:    ghostHov ? 'translateY(-2px)' : 'none',
                outline:      'none',
                letterSpacing:'0.01em',
              }}
              onMouseEnter={() => setGhostHov(true)}
              onMouseLeave={() => setGhostHov(false)}
              onFocus={() => setGhostHov(true)}
              onBlur={()  => setGhostHov(false)}
              onClick={scrollToBooks}
            >
              {t('HERO_CTA2')}
            </button>
          </div>

          {/* Stats row */}
          <div style={{
            display:       'flex',
            gap:           isMobile ? '20px' : '36px',
            flexWrap:      'wrap',
            paddingBottom: isMobile ? '0' : '40px',
            ...heroAnim(800),
          }}>
            <StatBlock icon={<IconBook  size={18} color={C.accent} />} num="7"               label={t('HERO_STAT1_LABEL')} />
            <StatBlock icon={<IconChart size={18} color={C.accent} />} num={`${overallPct}%`} label={t('HERO_STAT2_LABEL')} />
            <StatBlock icon={<IconWifi  size={18} color={C.accent} />} num="7"               label={t('HERO_STAT3_LABEL')} />
          </div>
        </div>

        {/* ── RIGHT COLUMN — 4 athlete cards + glowing curve + premium stat cards ── */}
        {!isMobile && (
          <div
            aria-hidden="true"
            style={{
              flex:           '0 0 44%',
              position:       'relative',
              display:        'flex',
              alignItems:     'center',
              justifyContent: 'center',
              animation:      'fadeIn 0.9s ease 0.3s both',
              minHeight:      '560px',
              alignSelf:      'stretch',
            }}
          >
            {/* Background glow blob */}
            <div style={{
              position:      'absolute',
              top:           '50%',
              left:          '50%',
              transform:     'translate(-50%, -50%)',
              width:         '480px',
              height:        '480px',
              borderRadius:  '50%',
              background:    `radial-gradient(circle, rgba(79,63,245,0.25) 0%, rgba(124,111,247,0.12) 40%, transparent 70%)`,
              filter:        'blur(10px)',
              pointerEvents: 'none',
              animation:     'pulseGlow2 8s ease-in-out infinite',
            }} />

            {/* Orbit ring decoration */}
            <div style={{
              position:      'absolute',
              top:           '50%',
              left:          '50%',
              width:         '500px',
              height:        '500px',
              borderRadius:  '50%',
              border:        `1px solid ${isDark ? 'rgba(124,111,247,0.08)' : 'rgba(79,63,245,0.06)'}`,
              transform:     'translate(-50%, -50%)',
              pointerEvents: 'none',
              animation:     'orbitRing 40s linear infinite',
              willChange:    'transform',
            }}>
              {/* Orbit dot */}
              <div style={{
                position:     'absolute',
                top:          '-4px',
                left:         '50%',
                transform:    'translateX(-50%)',
                width:        '8px',
                height:       '8px',
                borderRadius: '50%',
                background:   C.accent2,
                boxShadow:    `0 0 12px 3px ${C.accent2}`,
              }} />
            </div>

            {/* Orbit ring 2 — inner, dashed, counter-rotating */}
            <div style={{
              position:      'absolute',
              top:           '50%',
              left:          '50%',
              width:         '340px',
              height:        '340px',
              borderRadius:  '50%',
              border:        `1px dashed ${isDark ? 'rgba(124,111,247,0.11)' : 'rgba(79,63,245,0.08)'}`,
              transform:     'translate(-50%, -50%)',
              pointerEvents: 'none',
              animation:     'orbitRing 28s linear infinite reverse',
              willChange:    'transform',
            }}>
              <div style={{
                position:     'absolute',
                bottom:       '-3px',
                left:         '50%',
                transform:    'translateX(-50%)',
                width:        '6px',
                height:       '6px',
                borderRadius: '50%',
                background:   C.accent3,
                boxShadow:    `0 0 10px 2px ${C.accent3}`,
                opacity:      0.8,
              }} />
            </div>

            {/* Orbit ring 3 — outer tilted ellipse, very slow */}
            <div style={{
              position:      'absolute',
              top:           '50%',
              left:          '50%',
              width:         '640px',
              height:        '380px',
              borderRadius:  '50%',
              border:        `1px solid ${isDark ? 'rgba(79,140,247,0.05)' : 'rgba(79,63,245,0.04)'}`,
              transform:     'translate(-50%, -50%) rotate(-18deg)',
              pointerEvents: 'none',
              animation:     'orbitRingTilt 62s linear infinite',
              willChange:    'transform',
            }}>
              <div style={{
                position:     'absolute',
                top:          '-3px',
                left:         '25%',
                width:        '5px',
                height:       '5px',
                borderRadius: '50%',
                background:   C.accent,
                boxShadow:    `0 0 14px 3px ${C.accent}`,
                opacity:      0.55,
              }} />
            </div>

            {/* Energy curves */}
            <EnergyCurves C={C} />

            {/* Floating stat cards — values come from live progress data */}
            {floatStats.map((s) => (
              <FloatStatCard
                key={s.label}
                label={s.label}
                value={s.value}
                sub={s.sub}
                pos={s.pos}
                delay={s.delay}
                C={C}
                isDark={isDark}
              />
            ))}

            {/* 4-card grid */}
            <div style={{
              position:   'relative',
              zIndex:     1,
              display:    'flex',
              alignItems: 'center',
              gap:        '8px',
              width:      '100%',
              padding:    '0 4px',
            }}>
              {ATHLETE_CARDS.map((card, i) => {
                const baseTransform = `translateY(${card.ty}) rotate(${card.rot})`;
                const midTransform  = `translateY(calc(${card.ty} - 8px)) rotate(${card.rot})`;
                return (
                  <div
                    key={i}
                    style={{
                      flex:        1,
                      aspectRatio: '3/4.4',
                      position:    'relative',
                      borderRadius:'18px',
                      overflow:    'hidden',
                      border:      '1px solid rgba(255,255,255,0.10)',
                      boxShadow:   '0 18px 44px rgba(0,0,0,0.55), inset 0 0 0 1px rgba(255,255,255,0.04)',
                      ['--card-transform'     as never]: baseTransform,
                      ['--card-transform-mid' as never]: midTransform,
                      animation: `floatCard ${4.2 + i * 0.35}s ease-in-out ${i * 0.15}s infinite`,
                    } as React.CSSProperties}
                  >
                    <img
                      src={card.src}
                      alt={`Athlete — ${card.label}`}
                      loading={i === 0 ? 'eager' : 'lazy'}
                      width={200}
                      height={295}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                    {/* Bottom gradient */}
                    <div style={{
                      position:      'absolute',
                      inset:         0,
                      background:    'linear-gradient(180deg, transparent 50%, rgba(8,12,20,0.92) 100%)',
                      pointerEvents: 'none',
                    }} />
                    {/* Top label badge */}
                    <div style={{
                      position:             'absolute',
                      top:                  '7px',
                      left:                 '7px',
                      padding:              '3px 8px',
                      borderRadius:         '6px',
                      background:           'rgba(13,18,32,0.55)',
                      border:               '1px solid rgba(255,255,255,0.10)',
                      backdropFilter:       'blur(8px)',
                      WebkitBackdropFilter: 'blur(8px)',
                      fontSize:             '0.58rem',
                      fontWeight:           700,
                      color:                'rgba(241,245,249,0.75)',
                      textTransform:        'uppercase',
                      letterSpacing:        '0.09em',
                    }}>
                      {card.statLabel}
                    </div>
                    {/* Bottom label */}
                    <div style={{
                      position:             'absolute',
                      left: '8px', right: '8px', bottom: '8px',
                      padding:              '8px 10px',
                      borderRadius:         '10px',
                      background:           'rgba(13,18,32,0.62)',
                      border:               '1px solid rgba(255,255,255,0.10)',
                      backdropFilter:       'blur(10px)',
                      WebkitBackdropFilter: 'blur(10px)',
                      display:              'flex',
                      alignItems:           'center',
                      justifyContent:       'center',
                      gap:                  '6px',
                      fontSize:             '0.78rem',
                      fontWeight:           700,
                      color:                '#f1f5f9',
                      letterSpacing:        '0.03em',
                    }}>
                      <card.Icon size={14} color="#f1f5f9" />
                      {card.label}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Mobile: horizontal scroll strip with fade edges */}
        {isMobile && (
          <div style={{ position: 'relative', width: '100%' }}>
            <div style={{
              display:        'flex',
              gap:            '10px',
              overflowX:      'auto',
              paddingBottom:  '4px',
              scrollbarWidth: 'none',
            }}>
              {ATHLETE_CARDS.map((card, i) => (
                <div
                  key={i}
                  aria-hidden="true"
                  style={{
                    position:     'relative',
                    width:        '130px',
                    flexShrink:   0,
                    aspectRatio:  '3/4.4',
                    borderRadius: '14px',
                    overflow:     'hidden',
                    border:       '1px solid rgba(255,255,255,0.10)',
                    boxShadow:    '0 8px 22px rgba(0,0,0,0.45)',
                  }}
                >
                  <img
                    src={card.src}
                    alt={`Athlete — ${card.label}`}
                    loading={i === 0 ? 'eager' : 'lazy'}
                    width={130}
                    height={191}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                  <div style={{
                    position:      'absolute',
                    inset:         0,
                    background:    'linear-gradient(180deg, transparent 55%, rgba(8,12,20,0.85) 100%)',
                    pointerEvents: 'none',
                  }} />
                  <div style={{
                    position:             'absolute',
                    left: '6px', right: '6px', bottom: '6px',
                    padding:              '6px 8px',
                    borderRadius:         '8px',
                    background:           'rgba(13,18,32,0.60)',
                    border:               '1px solid rgba(255,255,255,0.10)',
                    backdropFilter:       'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)',
                    display:              'flex',
                    alignItems:           'center',
                    justifyContent:       'center',
                    gap:                  '4px',
                    fontSize:             '0.72rem',
                    fontWeight:           700,
                    color:                '#f1f5f9',
                    letterSpacing:        '0.02em',
                  }}>
                    <card.Icon size={12} color="#f1f5f9" />
                    {card.label}
                  </div>
                </div>
              ))}
            </div>

            {/* Fade out edge on right */}
            <div style={{
              position:      'absolute',
              top:           0,
              right:         0,
              bottom:        0,
              width:         '48px',
              background:    `linear-gradient(to left, ${C.bg} 0%, transparent 100%)`,
              pointerEvents: 'none',
            }} />
          </div>
        )}
      </section>

      {/* ════════════════════════ TRUST BAR ══════════════════════════════ */}
      <div
        ref={trustBarRef}
        style={{
          maxWidth:       '1280px',
          margin:         '0 auto',
          padding:        isMobile ? '32px 24px' : '48px 48px',
          borderTop:      `1px solid ${C.border}`,
          display:        'flex',
          alignItems:     'center',
          justifyContent: 'center',
          gap:            isMobile ? '14px' : '28px',
          flexWrap:       'wrap',
          ...revealStyle(inView.trustBar),
        }}
      >
        <span style={{
          fontSize:      '0.88rem',
          color:         C.muted,
          fontWeight:    500,
          letterSpacing: '0.01em',
        }}>
          Trusted by athletes and coaches in 50+ countries
        </span>

        {/* Overlapping circular athlete avatars */}
        <div style={{ display: 'flex', alignItems: 'center' }} aria-hidden="true">
          {TRUST_AVATARS.map((src, i) => (
            <div
              key={i}
              style={{
                width:        isMobile ? '28px' : '32px',
                height:       isMobile ? '28px' : '32px',
                borderRadius: '50%',
                overflow:     'hidden',
                border:       `2px solid ${isDark ? 'rgba(8,12,20,0.9)' : 'rgba(248,250,252,0.9)'}`,
                flexShrink:   0,
                marginLeft:   i > 0 ? (isMobile ? '-9px' : '-10px') : '0',
                background:   C.surface,
                boxShadow:    '0 2px 8px rgba(0,0,0,0.45)',
              }}
            >
              <img
                src={src}
                alt=""
                style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top center' }}
              />
            </div>
          ))}
        </div>

        {/* +50K stat */}
        <span style={{
          fontWeight:           900,
          fontSize:             '1.65rem',
          letterSpacing:        '-0.02em',
          background:           `linear-gradient(135deg, ${C.accent}, ${C.accent2})`,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor:  'transparent',
          backgroundClip:       'text',
        }}>
          +50K
        </span>
      </div>

      {/* ══════════════════════ BOOKS SECTION ════════════════════════════ */}
      <div
        id="books-section"
        ref={booksRef}
        style={{
          maxWidth: '1280px',
          margin:   '0 auto',
          padding:  isMobile ? '32px 24px 64px' : '48px 48px 96px',
        }}
      >
        {/* Section header */}
        <div
          ref={booksSectionRef}
          style={{
            display:      'flex',
            alignItems:   'baseline',
            gap:          '12px',
            marginBottom: '36px',
            ...revealStyle(inView.booksSection),
          }}
        >
          <h2 style={{
            fontSize:      '1.6rem',
            fontWeight:    800,
            color:         C.text,
            margin:        0,
            letterSpacing: '-0.02em',
          }}>
            {t('BOOKS_HEADING')}
          </h2>
          {!loading && !error && (
            <span style={{
              fontSize:      '0.82rem',
              color:         C.muted,
              fontWeight:    600,
              padding:       '3px 11px',
              borderRadius:  '999px',
              background:    isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)',
              border:        `1px solid ${C.border}`,
              letterSpacing: '0.02em',
            }}>
              {books.length} {t('BOOKS_AVAILABLE')}
            </span>
          )}
        </div>

        {/* Loading */}
        {(loading || !authLoaded) && (
          <div style={{ textAlign: 'center', padding: '80px 24px' }} role="status" aria-label="Loading books">
            <div style={{
              width:          '36px',
              height:         '36px',
              borderRadius:   '50%',
              border:         `3px solid ${C.border}`,
              borderTopColor: C.accent,
              animation:      'esp-spin 0.8s linear infinite',
              margin:         '0 auto 16px',
            }} />
            <p style={{ color: C.muted, margin: 0 }}>{t('BOOKS_LOADING')}</p>
          </div>
        )}

        {/* Error */}
        {!loading && authLoaded && error && (
          <div style={{ textAlign: 'center', padding: '80px 24px' }} role="alert">
            <div style={{
              width:          '52px',
              height:         '52px',
              borderRadius:   '50%',
              background:     'rgba(248,113,113,0.1)',
              border:         '1px solid rgba(248,113,113,0.25)',
              display:        'flex',
              alignItems:     'center',
              justifyContent: 'center',
              margin:         '0 auto 16px',
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>
            <p style={{ fontWeight: 700, color: C.text, margin: '0 0 8px' }}>Something went wrong</p>
            <p style={{ color: C.muted, margin: '0 0 20px' }}>{error}</p>
            <button
              style={{
                padding:      '10px 24px',
                borderRadius: '8px',
                background:   C.accent,
                color:        '#fff',
                fontWeight:   700,
                fontSize:     '0.9rem',
                border:       'none',
                cursor:       'pointer',
                fontFamily:   'inherit',
              }}
              onClick={retryFetch}
            >
              Retry
            </button>
          </div>
        )}

        {/* Empty */}
        {!loading && authLoaded && !error && books.length === 0 && (
          <div style={{ textAlign: 'center', padding: '80px 24px' }}>
            <div style={{
              width:          '52px',
              height:         '52px',
              borderRadius:   '50%',
              background:     isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)',
              border:         `1px solid ${C.border}`,
              display:        'flex',
              alignItems:     'center',
              justifyContent: 'center',
              margin:         '0 auto 16px',
            }}>
              <IconBook size={22} color={C.muted} />
            </div>
            <p style={{ fontWeight: 700, color: C.text, margin: '0 0 8px' }}>No books available yet</p>
            <p style={{ color: C.muted, margin: 0 }}>Check back soon — new content is on the way.</p>
          </div>
        )}

        {/* Books list */}
        {!loading && authLoaded && !error && books.length > 0 && (
          <div
            style={{
              display:       'flex',
              flexDirection: 'column',
              gap:           '20px',
              ...revealStyle(inView.booksSection, 120),
            }}
          >
            {books.map((book) => (
              <BookCard
                key={book.id}
                bookId={book.id}
                title={book.title}
                description={book.description}
                coverUrl={book.coverUrl}
                unitCount={book.units}
                level={book.level}
                tier={book.tier}
                unitTitles={book.unitTitles}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}